/**
 * Chart Message Component
 * Renders charts in chat messages using the same components as muscle group charts
 */

import { memo } from 'react'
import { Line, LineChart, XAxis, YAxis } from 'recharts'
import { cn } from '../../lib/utils'
import { Card } from '../ui/card'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../ui/chart'

interface ChartMessageProps {
  chartConfig: {
    type: 'chart'
    data: Array<{ x: string; y: number; smoothed?: number }>
    color: string
    smoothed: boolean
    title?: string
    x_label?: string
    y_label?: string
    dataKeys: {
      x: string
      y: string
      raw?: string
    }
  }
  _summary: string
  className?: string
}

const ChartMessageComponent = ({ chartConfig, _summary, className }: ChartMessageProps) => {
  const { data, color, smoothed, title, x_label, y_label, dataKeys } = chartConfig

  // Create chart configuration for the ChartContainer
  const chartContainerConfig = {
    [dataKeys.y]: {
      label: smoothed ? (title ? `${title} (Smoothed)` : 'Smoothed') : title || 'Data',
      color: color
    },
    ...(smoothed &&
      dataKeys.raw && {
        [dataKeys.raw]: {
          label: title ? `${title} (Raw)` : 'Raw Data',
          color: `${color}50`
        }
      })
  }

  return (
    <div className={cn(className)}>
      <Card className="bg-background dark:bg-input/30 border-border/50 rounded-lg border p-4">
        {title && (
          <div className="mb-3">
            <h4 className="text-sm font-medium">{title}</h4>
          </div>
        )}

        <div className="relative mb-3 h-40 w-full">
          {/* Minimal axis labels at edges */}
          {x_label && (
            <div className="text-muted-foreground absolute -bottom-4 left-1/2 z-10 -translate-x-1/2 transform text-[10px] font-normal">
              {x_label}
            </div>
          )}
          {y_label && (
            <div className="text-muted-foreground absolute top-1/2 -left-4 z-10 origin-center -translate-y-1/2 -rotate-90 transform text-[10px] font-normal whitespace-nowrap">
              {y_label}
            </div>
          )}

          <ChartContainer
            config={chartContainerConfig}
            className="mb-auto ml-auto h-[calc(100%-32px)] w-[calc(100%-32px)]"
          >
            <LineChart data={data} margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
              <XAxis dataKey={dataKeys.x} hide />
              <YAxis hide domain={['dataMin - 10%', 'dataMax + 10%']} />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    labelFormatter={(value) => {
                      // Try to format as date if it looks like a date
                      const dateRegex = /^\d{4}-\d{2}-\d{2}$/
                      if (typeof value === 'string' && dateRegex.test(value)) {
                        return new Date(value).toLocaleDateString()
                      }
                      return String(value)
                    }}
                    formatter={(value, name) => [
                      <span key="value" className="font-mono font-medium">
                        {Number(value).toLocaleString()}
                      </span>,
                      <span key="label" className="text-muted-foreground">
                        {chartContainerConfig[name as string]?.label || name}
                      </span>
                    ]}
                  />
                }
              />
              {smoothed && dataKeys.raw ? (
                <>
                  {/* Raw data line (background) */}
                  <Line
                    dataKey={dataKeys.raw}
                    stroke={`${color}30`}
                    strokeWidth={1.5}
                    dot={{
                      fill: `${color}50`,
                      strokeWidth: 0,
                      r: 2
                    }}
                    activeDot={false}
                  />
                  {/* Smoothed trend line (prominent) - no dots for cleaner look */}
                  <Line
                    dataKey={dataKeys.y}
                    stroke={color}
                    strokeWidth={3}
                    dot={false}
                    activeDot={{
                      r: 6,
                      fill: color,
                      strokeWidth: 2,
                      stroke: 'hsl(var(--background))'
                    }}
                  />
                </>
              ) : (
                /* Single data line */
                <Line
                  dataKey={dataKeys.y}
                  stroke={color}
                  strokeWidth={3}
                  dot={{
                    fill: color,
                    strokeWidth: 2,
                    r: 4
                  }}
                  activeDot={{
                    r: 6,
                    fill: color,
                    strokeWidth: 2,
                    stroke: 'hsl(var(--background))'
                  }}
                />
              )}
            </LineChart>
          </ChartContainer>
        </div>
      </Card>
    </div>
  )
}

export const ChartMessage = memo(ChartMessageComponent)
